import {
  hotelAccessCodeCrudConfig,
  type HotelAccessCode,
} from '@/config/objects/hotel-access-codes';
import { CrudDetailView, PageContainer, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Hotel Access Code detail page
 */
export function HotelAccessCodeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  /**
   * Delete hotel access code mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (
      hotelAccessCodeId: string,
    ): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/hotel-access-codes/${hotelAccessCodeId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        // Invalidate hotel access codes list to refresh data
        queryClient.invalidateQueries({ queryKey: ['HotelAccessCode'] });
        navigate('/hotel-access-codes');
      } else {
        throw new Error(response.error || 'Failed to delete access code');
      }
    },
  });

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Access Code Not Found</h2>
          <p className="text-muted-foreground">The requested access code could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form for this hotel access code
   */
  const handleEdit = (hotelAccessCode: HotelAccessCode) => {
    navigate(`/hotel-access-codes/${hotelAccessCode.id}/edit`);
  };

  /**
   * Handle hotel access code deletion
   */
  const handleDelete = async (hotelAccessCode: HotelAccessCode) => {
    return toast.promise(deleteMutation.mutateAsync(hotelAccessCode.id), {
      loading: 'Deleting access code...',
      success: 'Access code deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete access code',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Hotel Access Codes', href: '/hotel-access-codes' },
    { label: 'Access Code', current: true },
  ];

  return (
    <PageContainer>
      <div className="space-y-6">
        <CrudDetailView
          config={hotelAccessCodeCrudConfig}
          itemId={id}
          breadcrumbs={breadcrumbs}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>
    </PageContainer>
  );
}
